/**
 * A ranked round, played against the server.
 *
 * ## Why this exists at all
 *
 * A solo round runs on a local `GameEngine`: it holds the deck, applies the
 * rules and keeps the clock, and nothing it produces is recorded against
 * anybody. A ranked round cannot work that way. The deck is the whole
 * multiplayer feature — two players get the same 52 cards — and a client that
 * holds the deck can see every card before it is dealt. So for ranked play the
 * server owns all of it and this class is the local end of that conversation.
 *
 * It presents the small part of `GameEngine`'s surface that `main.ts` actually
 * uses, so the renderer, the choreography and the HUD do not know the
 * difference. The one thing it cannot fake is that a play is not decided here:
 * `playOn` returns a promise.
 *
 * ## Why it predicts
 *
 * It used to wait: tap, ask, animate the answer. The reasoning was that
 * predicting adds a second opinion about the rules to a design that had just
 * been given exactly one. That reasoning was wrong in its premise. Predicting
 * here does not write a second rulebook — it calls `resolvePile` and
 * `applyOutcome`, the same pure functions the server calls, on inputs the
 * player can already see. There is one opinion, evaluated twice.
 *
 * And the wait was felt. A player in India reported tapping a pile and having
 * nothing happen; a second in Arizona reported the same at a tenth the
 * distance. Waiting put a full round trip in front of the animation, and
 * `playOn` refused any tap arriving during it, so the fix the player reached
 * for — tap again — was also swallowed.
 *
 * So a play now resolves locally and animates immediately, and the request goes
 * out behind it. The round trip hides under the throw and the resolve, which
 * together run several times longer than any plausible ping. The server stays
 * authoritative in the way that matters: it owns the deck, it decides, and its
 * answer overwrites this one on arrival. Where they disagree — in practice only
 * when the clock ran out mid-tap — `onDiverge` rebuilds the board from truth.
 *
 * Only one play can be outstanding, and not because a flag says so: the next
 * card is not known here until the answer lands, so there is nothing to play
 * until it does.
 *
 * ## The wire: moves by index, over a socket, resent until they land
 *
 * Every move has a place in the round: its index, the count of moves before it.
 * `#queue` holds the moves the server has not acknowledged and `#acked` is how many
 * it has, so a sync always says "from `#acked`, these moves, and deal me card `n`"
 * (`@/net/deal`). That makes a resend safe whatever happened to the first try: the
 * server skips what it already applied and answers with the same hand. A lost
 * attempt (timeout, dropped socket, dead network) is therefore never a failed
 * play. It is resent, at the same index, 250/500/1000/2000 ms apart and then every
 * 2 s, for as long as it takes; a move is never applied twice, and never dropped.
 *
 * What the server can refuse is a different thing: `move-gap` and `move-conflict`
 * (this end and the server disagree about what was played), a move it will not
 * take. All of those are answered the same way as a prediction that turned out
 * wrong: ask the server how things stand, rebuild from that, carry on.
 * `game-over` means the clock (or the last card) ended the round: finish it from
 * the server's own account of it.
 *
 * ## When the answer does not come
 *
 * The clock keeps running; nothing here pauses it, and the server's does not
 * stop either. If the answer a play is owed (or the verdict on a round whose
 * clock ran out) has not arrived `RECONNECT_AFTER_MS` of wall time after it was owed (a timer,
 * not the frame loop: a table starved of frames must still say it), the table
 * is told (`onReconnecting`) to say so, and told again the moment play can
 * continue.
 *
 * ## Coming back
 *
 * A refresh loses the session id and its token, never the round: `resume` asks
 * the server for the player's own live round on a challenge (cookie auth) and
 * picks the board up from the view it returns. Nothing about the deck is needed
 * here, which is the point.
 *
 * ## The deck it is not allowed to see
 *
 * The renderer still draws the stack under the face-up card, and every sprite
 * is checked out of the pool by card id. The unseen ones get `hiddenCardId`
 * placeholders — stable, unique, and outside the real deck's namespace so they
 * can never collide with a card on the table. They are only ever drawn face
 * down, and the atlas answers with the back when asked for their face.
 */

import type { EventBus } from '@/core/bus';
import {
  hiddenCardId,
  PILE_COUNT,
  type CardId,
  type FinalScore,
  type GamePhase,
  type GameSnapshot,
  type Resolution,
  MAX_BUSTS,
  ROUND_SECONDS,
} from '@/game/types';
import { DECK_SIZE } from '@/game/deck';
import { levelFor, levelThreshold } from '@/game/levels';
import { resolvePile, totalsOf, type PileOutcome } from '@/game/rules';
import {
  applyOutcome,
  initialScoreState,
  streakLevelOf,
  type ScoredOutcome,
  type ScoreState,
} from '@/game/scoring';
import {
  DEAL_TIMEOUT_MS,
  challengeView,
  forfeitSession,
  isDefinite,
  liveSession,
  openSession,
  readSession,
  type ChallengeView,
  type SessionStep,
  type SessionView,
  type Submission,
} from '@/net/api';
import { backoffMs, DealLink, newDriverId, type HandReply, type Move } from '@/net/deal';

/**
 * How long, in wall-clock ms, a table may sit owed an answer before it says it is reconnecting.
 *
 * Owed: the next card after a play, or the verdict on a round whose clock has run out. An honest round trip is
 * a fraction of this, so the notice never shows on a healthy connection; a lost one shows it within a beat of
 * the player noticing. A timer rather than a count of ticks: a device that is dropping frames ticks slowly, and
 * the answer is late by the wall clock, not by the game's.
 */
export const RECONNECT_AFTER_MS = 3000;

/** The least time between two reads the table asks for on its own (intro window, expired clock). */
const POLL_EVERY_MS = 500;

/** How many times  asks the challenge's record before it gives up (about a minute and a half). */
const RESULT_TRIES = 48;

/** Patience for a call that can end the round: the server settles it before it answers. */
export const SETTLE_TIMEOUT_MS = 10_000;

/** A pause that a test can stand on. */
const sleep = (ms: number): Promise<void> => new Promise((resolve) => globalThis.setTimeout(resolve, ms));

const isPlay = (move: Move): boolean => 'pile' in move;

/**
 * The standings as the challenge's own page tells them: what a client that never saw the settling reply has.
 * Thinner than the reply (no XP beat, no chests: those were returned once and only to that reply), but the
 * verdict and the money are all there.
 */
function submissionFromChallenge(view: ChallengeView): Submission {
  const me = view.me;
  const them = view.opponent;
  const opponent =
    them && them.finished && them.score !== null
      ? {
          name: them.name,
          score: them.score,
          ...(them.level === null ? {} : { level: them.level }),
          createdAt: view.resolvedAt ?? '',
        }
      : null;
  const outcome =
    view.outcome === 'won' ? 'win' : view.outcome === 'lost' ? 'loss' : view.outcome === 'draw' ? 'draw' : 'unopposed';
  return {
    submitted: true,
    score: me?.score ?? 0,
    outcome,
    opponent,
    results: [],
    settled: view.result ? { won: view.result.won, payout: view.result.payout, stake: view.entryFee } : null,
  };
}

/**
 * The last known board, ended: the round the server says is over when its own account of it is gone (the
 * session was dropped) and only the challenge's recorded score and level are left. Nothing else is known, so
 * nothing else is claimed: the numbers that are not real are zero and the result is flagged
 * `detailsUnavailable`, which the results screen reads as "show the score and the verdict, hide the rows".
 */
function endedView(last: SessionView, score: number, level: number): SessionView {
  const final: FinalScore = {
    base: score,
    perfectGameBonus: 0,
    noBustBonus: 0,
    timeBonus: 0,
    total: score,
    busts: last.busts,
    secondsRemaining: 0,
    cardsRemaining: last.cardsRemaining,
    perfectGame: false,
    reason: 'timeout',
    clears: 0,
    bestStreak: 0,
    plays: 0,
    levelReached: level,
    detailsUnavailable: true,
  };
  return { ...last, phase: 'over', activeCard: null, secondsRemaining: 0, canUndo: false, score, final };
}

/**
 * Why a round could not be opened or picked up. `definite` is the server's own "no" (an answered 4xx): nothing
 * was created or nothing is live. Without it (a timeout, a dropped connection, a 5xx, a 429) the server may have
 * done the thing and lost the reply, and the caller must not act as if it had not.
 */
export interface RemoteRefusal {
  readonly error: string;
  readonly definite: boolean;
  /**
   * The server's own account that there is no live round FOR THIS PLAYER: `no-live-session` from the lookup, or
   * `not-signed-in`. The only answers on which a saved round is forgotten. Any other refusal, a definite one
   * included (a proxy's 404, a WAF's 403 during a deploy), says nothing about whether the round is live.
   */
  readonly gone: boolean;
}

/** Whether a failed call says "there is no live round for you": exactly those two codes, answered with their status. */
function saysGone(result: { error: string; status?: number }): boolean {
  return (
    (result.status === 404 && result.error === 'no-live-session') ||
    (result.status === 401 && result.error === 'not-signed-in')
  );
}

export class RemoteEngine {
  readonly #bus: EventBus;
  readonly #session: { id: string; token: string };
  readonly #challengeId: string;
  readonly #link: DealLink;
  /** Names this instance to the server (deal.ts `checkDriver`): which tab or device has the table. */
  readonly #driver = newDriverId();
  /** The server has not yet answered a call of this engine's: until it has, every call claims the round. */
  #claimed = false;
  /** Picked up from the server's own account of a round already in progress (`resume`), not dealt. */
  readonly #resumed: boolean;

  #view: SessionView;
  /** Ticked locally so the clock reads smoothly between answers. */
  #clock: number;
  #result: Submission | null = null;
  #ended = false;
  /** The table is gone (`dispose`), or the round cannot be reached at all (`onLost`): nothing more is sent. */
  #stopped = false;
  /** Guards `undo`, `forfeit`: they have no prediction to hide behind. */
  #busy = false;
  /**
   * Score as predicted here, reconciled against every answer.
   *
   * `score` and `busts` arrive with each view and are overwritten from it.
   * `streak` does not — the view carries only the capped *level* — so the raw
   * count is carried locally. It is derivable without the deck: a resolution
   * that scores extends the run and one that does not breaks it, and every
   * resolution passes through here. `#absorb` still cross-checks it against the
   * level the server reports.
   */
  #score: ScoreState;
  /** The score to restore on undo, captured before the play that moved it. */
  #undoScore: ScoreState | null = null;

  /** Moves the server has applied, as far as this end knows: the index of `#queue[0]`. */
  #acked: number;
  /** Moves made here and not yet acknowledged, in order. See the header. */
  readonly #queue: Move[] = [];
  /** A read has been asked for (the intro window, an expired clock) and not yet answered. */
  #wantRead = false;
  #nextReadAt = 0;
  #pumping = false;
  #idle: Promise<void> = Promise.resolve();
  /** Whether the last answer was taken as it stood (not a rebuild from the server's board). */
  #clean = false;

  /** When the round started on this client's clock, for the `tMs` every move carries. */
  readonly #startedAt: number;
  /** Fires `RECONNECT_AFTER_MS` after an answer became owed. See `#syncOwed`. */
  #owedTimer: ReturnType<typeof setTimeout> | null = null;
  #reconnecting = false;

  /**
   * Called when the server's answer contradicts the predicted one (or the board was rebuilt from the
   * server's own), with the true board already absorbed. Set by the caller to whatever rebuilds the table
   * from `snapshot()`.
   */
  onDiverge: (() => void) | null = null;
  /** The table should say it is reconnecting (true), or stop saying it (false). Fires on change only. */
  onReconnecting: ((reconnecting: boolean) => void) | null = null;
  /**
   * Another tab or device of the same player took the round over. This engine has stopped (nothing more is sent,
   * nothing retried); the caller leaves the table for the challenge page.
   */
  onTakenOver: (() => void) | null = null;
  /** The round cannot be reached any more (the server does not know it, or the sign-in is gone). */
  onLost: ((why: string) => void) | null = null;

  /** Last whole second announced, so `tick` fires once per second as it does
   * on the local engine — the HUD's clock is driven by that event and by
   * nothing else. */
  #lastSecond: number;
  /**
   * The cards the round opened with: 52 (a stacked deck in a test is its own size). Counted off the opening view
   * rather than assumed, and the deck's size on a resume, where the view is mid-round, so a placeholder is
   * never reused inside one round.
   */
  readonly #deckSize: number;

  private constructor(
    bus: EventBus,
    opened: { sessionId: string; token: string; view: SessionView; seq: number },
    challengeId: string,
    resumed: boolean,
  ) {
    this.#bus = bus;
    this.#session = { id: opened.sessionId, token: opened.token };
    this.#challengeId = challengeId;
    this.#resumed = resumed;
    const view = opened.view;
    this.#view = view;
    this.#deckSize = resumed ? DECK_SIZE : view.cardsRemaining;
    this.#clock = view.secondsRemaining;
    this.#lastSecond = view.secondsRemaining;
    this.#acked = opened.seq;
    this.#score = {
      ...initialScoreState(),
      score: view.score,
      busts: view.busts,
      ...(resumed ? { streak: this.#streakFor(view.streakLevel) } : {}),
    };
    // A resumed round started `elapsed` ago, by its own clock.
    const elapsedMs = resumed ? Math.max(0, ROUND_SECONDS * 1000 - view.secondsRemaining * 1000) : 0;
    this.#startedAt = performance.now() - elapsedMs;
    this.#link = new DealLink(opened.sessionId, { onTakenOver: () => this.#takenOver() });
  }

  /** Open a round on a challenge, or null if the server would not deal one. */
  static async open(
    bus: EventBus,
    input: { challengeId: string },
  ): Promise<{ engine: RemoteEngine } | RemoteRefusal> {
    const opened = await openSession(input);
    if (!opened.ok) return { error: opened.error, definite: isDefinite(opened), gone: saysGone(opened) };
    const { value } = opened;
    return {
      engine: new RemoteEngine(
        bus,
        { ...value, seq: value.seq ?? 0 },
        input.challengeId,
        false,
      ),
    };
  }

  /**
   * Pick up the player's own live round on a challenge, after a refresh. The error is the server's
   * (`no-live-session` when the round ran out or was recorded while the page was away).
   */
  static async resume(
    bus: EventBus,
    input: { challengeId: string },
  ): Promise<{ engine: RemoteEngine } | RemoteRefusal> {
    const live = await liveSession(input.challengeId);
    if (!live.ok) return { error: live.error, definite: isDefinite(live), gone: saysGone(live) };
    const { value } = live;
    return {
      engine: new RemoteEngine(
        bus,
        { ...value, seq: value.seq ?? 0 },
        input.challengeId,
        true,
      ),
    };
  }

  /** Stop talking: close the socket, stop retrying. For a round the table has moved on from. */
  dispose(): void {
    this.#stopped = true;
    this.#link.close();
    this.#syncOwed();
  }

  /** The challenge this round is on. */
  get challengeId(): string {
    return this.#challengeId;
  }

  /** The standings, once the server has recorded the round and this end has them. See `collectResult`. */
  get result(): Submission | null {
    return this.#result;
  }

  /** True while the table is saying it is reconnecting. */
  get reconnecting(): boolean {
    return this.#reconnecting;
  }

  /** The wire the next call would use (for diagnostics and tests). */
  get transport(): 'ws' | 'http' {
    return this.#link.transport;
  }

  get phase(): GamePhase {
    return this.#view.phase;
  }

  get isOver(): boolean {
    return this.#view.phase === 'over';
  }

  get final(): FinalScore | null {
    return this.#view.final;
  }

  start(): GameSnapshot {
    // The round is on the table: open its socket (and not before: see `DealLink.open`).
    this.#link.open();
    const snapshot = this.snapshot();
    this.#bus.emit({ type: 'game:start', snapshot });
    // A table picked up after a refresh claims the round NOW, not at its first play (a freshly dealt one does at
    // its opening sync): until the server has heard from this driver the previous one still has the table, and a
    // Forfeit tapped first would be a stranger's.
    if (this.#resumed) void this.sync();
    return snapshot;
  }

  /** The full length of the round and the cards it opened with: what a table resumed mid-round needs to size itself. */
  get roundSize(): { seconds: number; cards: number } {
    return { seconds: ROUND_SECONDS, cards: this.#deckSize };
  }

  /**
   * The board as the renderer needs it.
   *
   * `stock` is the shape the renderer expects — the active card first, then the
   * rest — but only the first entry is a real card. The rest are placeholders,
   * and the list is exactly as long as the server says the deck is, so the
   * stack thins as it is played.
   */
  snapshot(): GameSnapshot {
    const view = this.#view;
    const stock: CardId[] = [];
    if (view.activeCard) stock.push(view.activeCard);
    // Placeholders shift as cards are consumed, the same way the real stock
    // does, so a sprite keeps its identity as the deck closes up over it.
    const consumed = this.#deckSize - view.cardsRemaining;
    for (let depth = stock.length; depth < view.cardsRemaining; depth++) {
      stock.push(hiddenCardId(consumed + depth));
    }

    return {
      phase: view.phase,
      activeCard: view.activeCard,
      stock,
      piles: view.piles.map((pile) => [...pile]),
      pileTotals: view.pileTotals.map((totals) => ({ ...totals })),
      score: view.score,
      busts: view.busts,
      secondsRemaining: this.#seconds(),
      streakLevel: view.streakLevel,
      level: levelFor(view.score),
      canUndo: view.canUndo,
    };
  }

  /**
   * Place the visible card, and tell the server it happened.
   *
   * Resolves — immediately, on the predicted outcome — or to null when the play
   * cannot be made here: no card, wrong phase, a pile out of range, or a play
   * still unanswered. The caller treats null exactly as it treats a local
   * engine saying no.
   */
  async playOn(pileIndex: number): Promise<Resolution | null> {
    const view = this.#view;
    if (this.isOver || this.#stopped || view.phase !== 'playing') return null;
    if (!Number.isInteger(pileIndex) || pileIndex < 0 || pileIndex >= PILE_COUNT) return null;
    const card = view.activeCard;
    if (card === null) return null;
    // Nearly unreachable: the next card is unknown until the answer lands, so
    // `activeCard` is already null above. It stands for the case where an
    // answer is slower than the animation it is hiding under.
    if (this.#queue.length > 0) return null;

    // Bounds-checked against PILE_COUNT above.
    const outcome = resolvePile(view.piles[pileIndex]!, card);
    const levelBefore = levelFor(this.#score.score);
    const scored = applyOutcome(this.#score, outcome);
    this.#undoScore = this.#score;
    this.#score = scored.state;

    const resolution: Resolution = {
      pile: pileIndex,
      card,
      cards: outcome.cards.slice(),
      totals: { ...outcome.totals },
      cleared: outcome.cleared,
      is21: outcome.is21,
      isFiveCard: outcome.isFiveCard,
      isWild: outcome.isWild,
      isBust: outcome.isBust,
      bonuses: [...outcome.bonuses],
      basePoints: scored.basePoints,
      streakLevel: scored.streakLevel,
      streakPoints: scored.streakPoints,
      streakLabel: scored.streakLabel,
      pointsGained: scored.pointsGained,
      score: scored.state.score,
      busts: scored.state.busts,
    };

    // The beat the renderer animates: the card has left the stock and is
    // sitting on the pile, before anything has cleared. Captured before the
    // prediction is applied, for the same reason the local engine captures it
    // before clearing — it is a board that only exists mid-play.
    const landing = this.#landing(resolution);
    this.#predict(pileIndex, outcome, scored);
    // After the prediction: the sync names the card it wants dealt by the board as it now stands.
    this.#queue.push({ pile: pileIndex, tMs: this.#tMs() });
    this.#syncOwed();
    this.#kick();

    this.#bus.emit({ type: 'card:play', pile: resolution.pile, card: resolution.card, snapshot: landing });
    this.#bus.emit({ type: 'pile:resolve', resolution, snapshot: this.snapshot() });
    if (resolution.pointsGained !== 0) {
      this.#bus.emit({
        type: 'score:change',
        score: resolution.score,
        delta: resolution.pointsGained,
        snapshot: this.snapshot(),
      });
    }
    // Predicted like everything else on this path. The level is a pure
    // function of the score we have just predicted, so if the score turns out
    // to be wrong the divergence check catches it and `cutToState` rewrites
    // both together — there is no separate way for a level to go stale.
    const levelAfter = levelFor(resolution.score);
    if (levelAfter > levelBefore) {
      this.#bus.emit({
        type: 'level:up',
        level: levelAfter,
        from: levelBefore,
        atScore: resolution.score,
        nextThreshold: levelThreshold(levelAfter + 1),
        snapshot: this.snapshot(),
      });
    }
    if (resolution.isBust) {
      this.#bus.emit({ type: 'bust', pile: resolution.pile, busts: resolution.busts, snapshot: this.snapshot() });
    }
    return resolution;
  }

  /**
   * Wait until every move made here has been answered, and the board is the server's.
   *
   * The caller needs this at exactly one point: the next card is not known
   * until the answer arrives, so the flip that follows a resolve has to wait
   * for it. Everything before that — the throw, the pile settling, a clear —
   * runs on the prediction and never waits at all. On a bad connection it
   * waits as long as the resends take; it returns early only for a round that
   * is over or cannot be reached, where there is nothing left to wait for.
   */
  async settled(): Promise<void> {
    while (this.#queue.length > 0 && !this.#over()) {
      this.#kick();
      await this.#idle;
    }
  }

  /** Apply the predicted outcome to the view the renderer reads. */
  #predict(pileIndex: number, outcome: PileOutcome, scored: ScoredOutcome): void {
    const view = this.#view;
    const piles = view.piles.map((pile, i) =>
      i === pileIndex ? (outcome.cleared ? [] : outcome.cards.slice()) : pile,
    );
    this.#view = {
      ...view,
      // Unknown until the server says. `settled` is what waits for it.
      activeCard: null,
      cardsRemaining: Math.max(0, view.cardsRemaining - 1),
      piles,
      // Recomputed rather than taken from `outcome.totals`, which are the
      // totals *including* the card — wrong for a pile that just cleared.
      pileTotals: piles.map((pile, i) => (i === pileIndex ? totalsOf(pile) : view.pileTotals[i]!)),
      score: scored.state.score,
      busts: scored.state.busts,
      streakLevel: streakLevelOf(scored.state.streak),
      canUndo: true,
    };
  }

  /* ------------------------------------------------------------ the pump -- */

  /** Nothing more will be sent: the round is over, or this end was told to stop, or it cannot be reached. */
  #over(): boolean {
    return this.#ended || this.#stopped;
  }

  #tMs(): number {
    return Math.max(0, Math.round(performance.now() - this.#startedAt));
  }

  /** Start sending, if it is not already. One pump per round: nothing is sent twice at once. */
  #kick(): void {
    if (this.#pumping || this.#over()) return;
    this.#pumping = true;
    this.#idle = this.#run();
  }

  /**
   * Send until there is nothing owed: the moves the server has not acknowledged, and any read asked for. A
   * lost attempt waits out the backoff and goes again with the same `fromIndex`.
   */
  async #run(): Promise<void> {
    let lost = 0;
    try {
      while (!this.#over() && (this.#queue.length > 0 || this.#wantRead)) {
        if ((await this.#attempt()) === 'answered') {
          lost = 0;
          continue;
        }
        await sleep(backoffMs(lost++));
      }
    } finally {
      this.#pumping = false;
    }
  }

  /** The card to ask for: the one that is face up once everything made here has been applied. */
  #handIndex(): number {
    const last = this.#queue.at(-1);
    const undoing = last !== undefined && !isPlay(last);
    return Math.max(0, this.#deckSize - this.#view.cardsRemaining - (undoing ? 1 : 0));
  }

  /**
   * Whether this call can be the one that ends the round (the last card, the last bust, a clock that has run out).
   * The server settles the round (a write to the ledger, XP, chests) before it answers that call, which takes
   * longer than an ordinary one: its patience is longer, so a slow settle is not mistaken for a lost reply.
   */
  #mayEndRound(): boolean {
    const view = this.#view;
    return view.cardsRemaining === 0 || this.#clock <= 0 || view.busts >= MAX_BUSTS || this.#score.busts >= MAX_BUSTS;
  }

  async #attempt(): Promise<'answered' | 'lost'> {
    const sent = this.#queue.slice();
    const asked = this.#wantRead;
    this.#wantRead = false;
    const outcome = await this.#link.sync({
      driver: this.#driver,
      claim: !this.#claimed,
      fromIndex: this.#acked,
      n: this.#handIndex(),
      moves: sent,
    }, this.#mayEndRound() ? SETTLE_TIMEOUT_MS : undefined);
    if (this.#over()) return 'answered';
    // The claim has taken effect only once the server ACCEPTED a call: it commits the change of driver with an
    // accepted call (deal.ts `commitDriver`), not with one it decided about and then refused (a move-conflict, a
    // bad-move, a game-not-playing). Until then every call keeps claiming, or a refused first call would leave
    // this engine a stranger at its next sync.
    if (outcome.kind === 'hand') this.#claimed = true;

    if (outcome.kind === 'hand') return this.#onHand(outcome.reply, sent);
    if (outcome.kind === 'refused') return this.#onRefused(outcome.code, sent, outcome.result);
    this.#wantRead ||= asked;
    return 'lost';
  }

  /** The server took the batch (or had already): everything it says is truth, and what it applied is acknowledged. */
  async #onHand(reply: HandReply, sent: readonly Move[]): Promise<'answered' | 'lost'> {
    // More or fewer moves applied than were sent means this end and the server have drifted (another tab on
    // the same round, say). Never guess which is right.
    if (reply.count !== this.#acked + sent.length) return this.#resync();

    const predicted = this.#view;
    this.#queue.splice(0, sent.length);
    this.#acked = reply.count;
    // A move was made while this was in flight (a poll's answer, a tap): the view says how things stood BEFORE
    // it, and taking it would undo the prediction on screen. The bookkeeping is done; the move's own answer
    // brings the view. A round that is over is the one view that is always the truth.
    if (this.#queue.length > 0 && !reply.view.final) {
      if (reply.result) this.#result = reply.result;
      return 'answered';
    }
    this.#absorb({ view: reply.view, ...(reply.result ? { result: reply.result } : {}) });

    // A play was predicted: the server's board has to be the one this end drew. Compared on what the player
    // can see, not on how it got there (the old per-play resolution check, on the view).
    const drifted =
      sent.some(isPlay) &&
      (reply.view.score !== predicted.score ||
        reply.view.busts !== predicted.busts ||
        reply.view.cardsRemaining !== predicted.cardsRemaining ||
        JSON.stringify(reply.view.piles) !== JSON.stringify(predicted.piles));
    this.#clean = !drifted;
    if (drifted) {
      this.#reseed();
      this.onDiverge?.();
    }
    this.#answered();
    this.#settle();
    return 'answered';
  }

  async #onRefused(code: string, sent: readonly Move[], result?: Submission): Promise<'answered' | 'lost'> {
    // An undo the rules refuse (nothing to take back, or one already taken since the last play) is not a
    // disagreement about the board: it changed nothing there, and this end predicted nothing for it. This server
    // answers it `bad-move` on the deal protocol, whatever the status the call came back with. Taken quietly.
    if (code === 'bad-move' && sent.length === 1 && !isPlay(sent[0]!)) return this.#undoRefused(sent);
    switch (code) {
      case 'game-over':
        // The round was settled by a call whose reply this end never saw: the server kept what it returned.
        if (result) this.#result = result;
        return this.#finish();
      case 'taken-over':
        this.#takenOver();
        return 'answered';
      // This end and the server disagree about what was played, or the server will not take a move this end
      // thought was legal (the clock ran out between the tap and the request): ask how things stand.
      case 'move-gap':
      case 'move-conflict':
      case 'bad-move':
      case 'game-not-playing':
      case 'hand-too-early':
      case 'no-such-hand':
      case 'bad-hand':
        return this.#resync();
      // The server no longer holds this round (it restarted, or swept it): its own record of the challenge
      // says whether it was settled, and the same lookup ends the round from that or gives it up.
      case 'game-not-found':
        return this.#resync();
      default:
        // unauthorized, forbidden, a malformed call: resending will not change the answer.
        this.#lose(code);
        return 'answered';
    }
  }

  /**
   * The server refused an undo. Nothing was predicted and nothing applied, so there is nothing to rebuild: no
   * resync, no "reconnecting", no lost round, no resend. The undo is dropped from the queue, the view stays as it
   * is with the undo switched off (the next answer from the server brings its own count), and the next play goes
   * on from the same index.
   */
  #undoRefused(sent: readonly Move[]): 'answered' {
    this.#queue.splice(0, sent.length);
    this.#view = { ...this.#view, canUndo: false };
    this.#answered();
    return 'answered';
  }

  /**
   * Truth won. Take the server's own account of the live round (its view, and how many moves it has applied),
   * drop what this end had queued, and hand the board to whoever can rebuild it.
   */
  async #resync(): Promise<'answered' | 'lost'> {
    const live = await liveSession(this.#challengeId);
    if (this.#over()) return 'answered';
    if (!live.ok) {
      if (live.error === 'no-live-session') return this.#finish();
      if (live.error === 'not-signed-in') {
        this.#lose(live.error);
        return 'answered';
      }
      return 'lost';
    }
    this.#queue.length = 0;
    this.#acked = live.value.seq ?? this.#acked;
    this.#absorb({ view: live.value.view });
    this.#rebuilt();
    return 'answered';
  }

  /**
   * The server says the round is over. Its view has the final, and the reply that settled it may have been
   * lost, so the standings are collected separately (`collectResult`). A round the server no longer holds is
   * read off the challenge's own record.
   */
  async #finish(): Promise<'answered' | 'lost'> {
    const read = await readSession(this.#session);
    if (this.#over()) return 'answered';
    if (read.ok) {
      this.#queue.length = 0;
      this.#acked = read.value.seq ?? this.#acked;
      this.#absorb(read.value);
      this.#rebuilt();
      return 'answered';
    }
    if (read.error !== 'no such session') return 'lost';

    const record = await challengeView(this.#challengeId);
    if (this.#over()) return 'answered';
    if (!record.ok) return 'lost';
    if (!record.value.me?.finished) {
      this.#lose('round-gone');
      return 'answered';
    }
    this.#queue.length = 0;
    this.#absorb({ view: endedView(this.#view, record.value.me.score, record.value.me.level) });
    this.#result ??= submissionFromChallenge(record.value);
    this.#rebuilt();
    return 'answered';
  }

  /** The board was replaced by the server's: the per-round tallies are re-seeded and the table is told. */
  #rebuilt(): void {
    this.#reseed();
    this.#clean = false;
    this.onDiverge?.();
    this.#answered();
    this.#settle();
  }

  /**
   * The per-round tallies are carried, not reset: the server's view does not report them (it has its own copy
   * and computes the XP award from that), so resyncing must not throw away what this end has counted.
   */
  #reseed(): void {
    const view = this.#view;
    this.#score = {
      ...this.#score,
      score: view.score,
      busts: view.busts,
      streak: this.#streakFor(view.streakLevel),
    };
    this.#undoScore = null;
  }

  /**
   * The smallest run that shows as this level.
   *
   * Exact below the cap, which is all that is asked of it: it is only used to
   * re-seed the count after the server has contradicted it, and above the cap
   * the count has no observable effect — `streakBonusOf` clamps to the level.
   */
  #streakFor(level: number): number {
    return level === 0 ? 0 : level + 1;
  }

  /** Another driver has the round: stop, quietly and for good. No retries; the table is told. */
  #takenOver(): void {
    if (this.#over()) return;
    this.#stopped = true;
    this.#link.close();
    this.#syncOwed();
    this.onTakenOver?.();
  }

  /** The round cannot be reached: nothing is sent again, nobody waits on it, and the table is told. */
  #lose(why: string): void {
    console.error('wild21: the round was lost', why);
    this.#stopped = true;
    this.#link.close();
    this.#syncOwed();
    this.onLost?.(why);
  }

  /* ---------------------------------------------------------- moves etc. -- */

  /**
   * Hand the round in. The server ends it as it stands and settles it; this
   * end takes the answer as it takes any other step, so the game-over that
   * follows is the real one, not a local guess about it.
   */
  async forfeit(): Promise<boolean> {
    await this.settled();
    if (this.isOver) return true;
    if (this.#stopped || this.#busy) return false;
    this.#busy = true;
    try {
      const step = await forfeitSession(this.#session, this.#acked, this.#driver, !this.#claimed);
      if (!step.ok) {
        if (step.status === 409 && step.error === 'taken-over') this.#takenOver();
        return false;
      }
      this.#absorb(step.value);
      this.#settle();
      // A forfeit that ended the round was applied, and with it the claim; one that did nothing was not.
      if (this.#ended) this.#claimed = true;
      // True only if the round ENDED. A 200 whose view carries no final is a
      // forfeit that did nothing — `#settle` is quiet about a view with no
      // final because every poll's view is one; here it is the failure, and
      // saying "done" over it left the sheet up on a live round.
      if (!this.#ended) console.error('wild21: forfeit answered without a final', step.value.view.phase);
      return this.#ended;
    } finally {
      this.#busy = false;
    }
  }

  /** Take the last play back, if the server still allows it. */
  async undo(): Promise<boolean> {
    // The server cannot take back a play it has not been told about, and this
    // end cannot know what the board looked like before one it has not had
    // answered. Both are the same wait.
    await this.settled();
    if (this.#busy || this.#over() || !this.#view.canUndo) return false;
    this.#busy = true;
    try {
      const restored = this.#undoScore;
      const before = this.#acked;
      this.#clean = false;
      this.#queue.push({ undo: true, tMs: this.#tMs() });
      this.#syncOwed();
      this.#kick();
      await this.settled();
      // Taken as it stood only if the server's reply said the undo was applied; otherwise the board was
      // rebuilt from the server's own and the table has already been told.
      if (!this.#clean || this.#acked !== before + 1) return false;
      // Exact, where `#absorb`'s cross-check against the reported level is only
      // exact below the cap. This is the value the run actually had.
      if (restored) this.#score = restored;
      this.#undoScore = null;
      this.#bus.emit({ type: 'undo', snapshot: this.snapshot() });
      return true;
    } finally {
      this.#busy = false;
    }
  }

  /**
   * Run the displayed clock down.
   *
   * Local, and only for display: the authoritative figure arrives with every
   * answer and overwrites this. It runs whether or not the server can be
   * reached, and nothing here ever stops it: the server's clock does not wait
   * for a connection. When it runs out the round is over on the server whether
   * or not the player does anything, so the result is collected rather than
   * waited for.
   */
  tick(deltaSeconds: number): void {
    if (this.#over() || !Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return;

    // Not playable yet. Nothing arrives to say when that changes, so ask —
    // throttled, and this stops the moment the answer says the round has begun.
    if (this.#view.phase !== 'playing') {
      this.#poll();
      return;
    }

    this.#clock = Math.max(0, this.#clock - deltaSeconds);

    // The HUD's clock is driven by this event and nothing else, so it has to be
    // emitted here exactly as the local engine emits it: once per whole second.
    const second = this.#seconds();
    if (second !== this.#lastSecond) {
      this.#lastSecond = second;
      this.#bus.emit({ type: 'tick', secondsRemaining: second, snapshot: this.snapshot() });
    }

    // Out of time here means out of time there. Collect the result rather than
    // waiting for a play that can no longer be made.
    if (this.#clock <= 0) this.#poll();

    // The verdict on an expired clock is owed from this moment (a play's answer from the play).
    this.#syncOwed();
  }

  /**
   * Ask the server how things stand. Resolves when the pump has had its first answer, or after one attempt's
   * patience: a table does not wait on a connection that is down, it keeps asking behind its back.
   *
   * Needed because this end learns nothing except by asking. The round begins in its intro window and becomes
   * playable on the server's clock, not on any message — so without this the client would sit at `dealing`
   * for ever and refuse every tap.
   */
  async sync(): Promise<void> {
    this.#poll();
    await Promise.race([this.#idle, sleep(DEAL_TIMEOUT_MS)]);
  }

  /** A read, at most every `POLL_EVERY_MS`; the pump sends it and keeps at it until it is answered. */
  #poll(): void {
    if (this.#over()) return;
    const now = performance.now();
    if (now < this.#nextReadAt) return;
    this.#nextReadAt = now + POLL_EVERY_MS;
    this.#wantRead = true;
    this.#kick();
  }

  /** An answer is owed: the next card after a play, or the verdict on a round whose clock has run out. */
  #isOwed(): boolean {
    if (this.#over()) return false;
    return this.#queue.length > 0 || (this.#view.phase === 'playing' && this.#clock <= 0 && !this.#view.final);
  }

  /**
   * Start the notice's timer when an answer becomes owed, and stand it down when it is not. The timer is
   * wall-clock on purpose (see `RECONNECT_AFTER_MS`); what it finds when it fires decides, so an answer that
   * came in the meantime cancels it without anyone having to remember to.
   */
  #syncOwed(): void {
    if (!this.#isOwed()) {
      if (this.#owedTimer !== null) globalThis.clearTimeout(this.#owedTimer);
      this.#owedTimer = null;
      this.#setReconnecting(false);
      return;
    }
    if (this.#owedTimer !== null || this.#reconnecting) return;
    this.#owedTimer = globalThis.setTimeout(() => {
      this.#owedTimer = null;
      if (this.#isOwed()) this.#setReconnecting(true);
    }, RECONNECT_AFTER_MS);
  }

  /** The answer came: whatever was owed is no longer, and a table that was saying so can stop. */
  #answered(): void {
    this.#syncOwed();
  }

  #setReconnecting(on: boolean): void {
    if (this.#reconnecting === on) return;
    this.#reconnecting = on;
    this.onReconnecting?.(on);
  }

  /** Take the server's word for everything. */
  #absorb(step: SessionStep): void {
    this.#view = step.view;
    this.#clock = step.view.secondsRemaining;
    this.#lastSecond = this.#seconds();
    if (step.result) this.#result = step.result;

    // The two the server reports are taken verbatim. The run length it does not
    // report, so the local count stands — unless the level it *does* report
    // says the count is wrong, in which case the server wins as it does
    // everywhere else.
    const streak =
      streakLevelOf(this.#score.streak) === step.view.streakLevel
        ? this.#score.streak
        : this.#streakFor(step.view.streakLevel);
    this.#score = { ...this.#score, score: step.view.score, busts: step.view.busts, streak };
  }

  /** Announce the end exactly once, whichever answer brought the news. */
  #settle(): void {
    const final = this.#view.final;
    if (!final || this.#ended) return;
    this.#ended = true;
    // Nothing more is sent: the round is over, and the socket has done its job.
    this.#link.close();
    this.#syncOwed();
    this.#bus.emit({ type: 'game:over', final, snapshot: this.snapshot() });
  }

  /**
   * The standings, once there are any: what the settling reply carried, or else the challenge's own record of
   * the round (a reply that never arrived — a dropped connection at the last card, a clock that ran out while
   * the player was away). Keeps asking while the server cannot be reached, so the verdict shows the moment it
   * can be; null only after `RESULT_TRIES` and for a round that is not over.
   */
  async collectResult(): Promise<Submission | null> {
    for (let attempt = 0; this.#result === null && this.#ended && attempt < RESULT_TRIES; attempt++) {
      if (this.#stopped) break;
      if (attempt > 0) await sleep(backoffMs(attempt - 1));
      if (this.#result !== null) break;
      const record = await challengeView(this.#challengeId);
      if (record.ok && record.value.me?.finished) this.#result = submissionFromChallenge(record.value);
    }
    return this.#result;
  }

  /** The clock as it is shown, rounded the way the local engine rounds it. */
  #seconds(): number {
    return Math.max(0, Math.ceil(this.#clock - 1e-6));
  }

  /** The intermediate board: card on the pile, nothing resolved yet. */
  #landing(resolution: Resolution): GameSnapshot {
    const snapshot = this.snapshot();
    const piles = snapshot.piles.map((pile, i) => (i === resolution.pile ? [...resolution.cards] : pile));
    return { ...snapshot, piles, phase: 'resolving' };
  }
}
